import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ShieldCheck, Leaf, Truck, Sparkles, Gift, ShoppingBasket as Basket, Mountain, X, ChefHat, Box, Utensils, Droplet } from "lucide-react";
import { api, fmtPrice } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import ProductCard from "@/components/ProductCard";
import OliveLeaves from "@/components/OliveLeaves";
import Reveal, { EASE } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import JourneySection from "@/components/JourneySection";
import OccasionDiscovery from "@/components/OccasionDiscovery";
import { ProductGridSkeleton } from "@/components/Skeletons";

const HERO_IMG = "https://images.unsplash.com/photo-1649696950895-f454bc7e6f80?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const CAT_VISUAL = {
  olive_oil: { img: "https://images.pexels.com/photos/17556893/pexels-photo-17556893.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200", icon: Droplet },
  olives_pickles: { img: "https://images.unsplash.com/photo-1760445528267-25140a7bd31b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", icon: Leaf },
  zaatar_spices: { img: "https://images.pexels.com/photos/8871293/pexels-photo-8871293.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200", icon: Sparkles },
  honey_jams: { img: "https://images.unsplash.com/photo-1644221362202-2e3e6b01d717?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", icon: Gift },
  soap_care: { img: "https://images.pexels.com/photos/7175389/pexels-photo-7175389.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200", icon: Leaf },
};

function SectionHead({ eyebrow, title, desc, to, toLabel }) {
  return (
    <div className="mb-10 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
      <div className="max-w-2xl">
        {eyebrow && <span className="mb-2 inline-block rounded-full bg-olive/8 px-3 py-1 text-xs font-semibold text-olive">{eyebrow}</span>}
        <h2 className="font-display text-2xl font-bold text-ink sm:text-3xl lg:text-4xl">{title}</h2>
        {desc && <p className="mt-3 text-sm text-ink/55 sm:text-base">{desc}</p>}
      </div>
      {to && (
        <Link to={to} className="link-underline flex shrink-0 items-center gap-1.5 font-medium text-olive">
          {toLabel} <ArrowLeft className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
}

const TRUST = [
  { icon: ShieldCheck, key: "trust1" },
  { icon: Leaf, key: "trust2" },
  { icon: Truck, key: "trust3" },
  { icon: Sparkles, key: "trust4" },
];

export default function Home() {
  const { t, L } = useLang();
  const [featured, setFeatured] = useState(null);
  const [featError, setFeatError] = useState(false);
  const [season, setSeason] = useState(null);
  const [categories, setCategories] = useState([]);
  const [geoSources, setGeoSources] = useState([]);
  const [table, setTable] = useState(null);
  const [heritage, setHeritage] = useState([]);
  const [activeSpot, setActiveSpot] = useState(null);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.06, 1.16]);
  const contentFade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const loadFeatured = () => {
    setFeatError(false);
    setFeatured(null);
    api.get("/products", { params: { sort: "rating", limit: 8 } })
      .then((r) => setFeatured(r.data))
      .catch(() => setFeatError(true));
  };

  useEffect(() => {
    loadFeatured();
    api.get("/products/season").then((r) => setSeason(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/geo-sources").then((r) => setGeoSources(r.data)).catch(() => {});
    api.get("/table").then((r) => setTable(r.data)).catch(() => {});
    api.get("/heritage").then((r) => setHeritage(r.data)).catch(() => {});
  }, []);

  const nablus = geoSources.find((g) => g.key === "nablus_mt");
  const otherTerrains = geoSources.filter((g) => g.key !== "nablus_mt");

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} data-testid="hero" className="relative flex min-h-[92svh] items-end overflow-hidden sm:min-h-screen sm:items-center">
        <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
          <img src={HERO_IMG} alt="المسجد الأقصى من بين أغصان الزيتون" fetchPriority="high" className="h-full w-full object-cover object-[50%_30%]" />
        </motion.div>
        <div className="hero-gradient absolute inset-0" />
        <div className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-olive-deep/90 via-olive-deep/35 to-transparent" aria-hidden="true" />
        <OliveLeaves count={14} />

        <motion.div
          initial={{ opacity: 0, scale: 0.7, y: 18 }}
          animate={{ opacity: [0, 1, 1, 0], scale: [0.7, 1, 1.05, 1.16], y: [18, 0, 0, -16] }}
          transition={{ duration: 2.3, times: [0, 0.28, 0.72, 1], ease: EASE }}
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center"
          aria-hidden="true"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: [0, 0.9, 0.7, 0], scale: [0.5, 1, 1.15, 1.35] }}
            transition={{ duration: 2.3, times: [0, 0.28, 0.72, 1], ease: EASE }}
            className="absolute h-64 w-64 rounded-full sm:h-80 sm:w-80"
            style={{ background: "radial-gradient(circle, rgba(184,153,71,0.5) 0%, rgba(194,143,89,0.22) 45%, transparent 70%)", filter: "blur(24px)" }}
          />
          <img src="/logo.png" alt="" className="relative h-28 w-28 rounded-[1.75rem] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)] sm:h-44 sm:w-44 sm:rounded-[2rem]" />
        </motion.div>

        <motion.div className="safe-x relative mx-auto w-full max-w-7xl pb-12 pt-28 sm:pb-0 sm:pt-24" style={{ opacity: contentFade }}>
          <div className="max-w-xl sm:max-w-3xl">
            <motion.span
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE, delay: 0.95 }}
              className="mb-4 inline-block rounded-full border border-ivory/30 bg-ivory/10 px-3.5 py-1 text-xs font-medium text-ivory backdrop-blur sm:mb-5 sm:px-4 sm:py-1.5 sm:text-sm">
              {t("tagline")}
            </motion.span>
            <h1 className="font-display text-[clamp(2.05rem,8.2vw,3.75rem)] font-extrabold leading-[1.22] text-ivory">
              <span className="mask-line">
                <motion.span className="block" initial={{ y: "115%" }} animate={{ y: 0 }}
                  transition={{ duration: 0.9, ease: EASE, delay: 1.1 }}>{t("hero_title1")}</motion.span>
              </span>
              <span className="mask-line">
                <motion.span className="block text-earth" initial={{ y: "115%" }} animate={{ y: 0 }}
                  transition={{ duration: 0.9, ease: EASE, delay: 1.25 }}>{t("hero_title2")}</motion.span>
              </span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE, delay: 1.4 }}
              className="mt-4 max-w-md text-[15px] leading-relaxed text-ivory/85 sm:mt-6 sm:text-lg">
              {t("hero_sub")}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE, delay: 1.55 }}
              className="mt-7 flex max-w-sm flex-col gap-3 sm:mt-9 sm:max-w-none sm:flex-row">
              <Link to="/store?category=olive_oil" data-testid="hero-cta-oil"
                className="btn-shine flex h-[52px] items-center justify-center gap-2 rounded-full bg-olive px-7 text-[15px] font-semibold text-ivory transition-[background-color,transform] duration-300 hover:bg-olive-light active:scale-[0.97] sm:h-auto sm:py-4 sm:text-base">
                {t("hero_cta1")} <ArrowLeft className="h-5 w-5" />
              </Link>
              <Link to="/store" data-testid="hero-cta-all"
                className="flex h-[52px] items-center justify-center gap-2 rounded-full border border-ivory/40 bg-ivory/10 px-7 text-[15px] font-semibold text-ivory backdrop-blur transition-colors hover:bg-ivory/20 active:scale-[0.97] sm:h-auto sm:py-4 sm:text-base">
                {t("hero_cta2")}
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* TRUST */}
      <section data-testid="trust-band" className="glass border-b border-olive/10">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-x-2 px-2 py-2 md:grid-cols-4">
          {TRUST.map((tr) => (
            <div key={tr.key} className="flex items-center justify-center gap-2.5 px-3 py-3 text-center">
              <tr.icon className="h-5 w-5 shrink-0 text-olive" />
              <span className="text-[13px] font-medium text-ink/80 sm:text-sm">{t(tr.key)}</span>
            </div>
          ))}
        </div>
      </section>

      <Marquee />

      {/* CATEGORIES BY PRODUCT TYPE */}
      <section className="mx-auto max-w-7xl px-6 py-16 sm:py-20">
        <Reveal>
          <SectionHead eyebrow={t("cat_eyebrow")} title={t("cat_title")} desc={t("cat_desc")}
            to="/store" toLabel={t("view_all")} />
        </Reveal>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5 lg:gap-5">
          {categories.map((c, i) => {
            const v = CAT_VISUAL[c.key] || {};
            const Icon = v.icon || Leaf;
            const featured = c.featured;
            return (
              <Reveal key={c.key} delay={i * 60} className={featured ? "col-span-2 lg:col-span-2 lg:row-span-2" : ""}>
                <Link to={`/store?category=${c.key}`} data-testid={`home-category-${c.key}`}
                  className={`group relative flex overflow-hidden rounded-3xl border border-olive/10 bg-olive-dark text-ivory ${featured ? "min-h-[220px] flex-col justify-end p-6 sm:min-h-[300px]" : "min-h-[150px] flex-col justify-end p-5"}`}>
                  <img src={v.img} alt={c.name_ar} loading="lazy" decoding="async"
                    className="absolute inset-0 h-full w-full object-cover opacity-45 transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-olive-deep/90 via-olive-deep/30 to-transparent" />
                  <div className="relative">
                    {featured && <span className="mb-2 inline-block rounded-full bg-gold px-2.5 py-1 text-[11px] font-bold text-olive-deep">{t("cat_featured")}</span>}
                    <Icon className={`mb-2 text-earth ${featured ? "h-8 w-8" : "h-6 w-6"}`} />
                    <h3 className={`font-display font-bold ${featured ? "text-2xl" : "text-base"}`}>{L(c, "name")}</h3>
                    <div className="mt-1 text-xs text-ivory/70">{c.count} {t("products_count")}</div>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="mx-auto max-w-7xl px-6 pb-16 sm:pb-20">
        <Reveal>
          <SectionHead eyebrow={t("pal_eyebrow")} title={t("pal_title")} desc={t("pal_desc")}
            to="/store" toLabel={t("view_all")} />
        </Reveal>
        {featError ? (
          <div className="rounded-3xl border border-dashed border-palred/30 py-16 text-center">
            <p className="text-ink/60">{t("error_generic")}</p>
            <button data-testid="feat-retry" onClick={loadFeatured} className="mt-4 rounded-full bg-olive px-6 py-2.5 text-sm font-semibold text-ivory">{t("retry")}</button>
          </div>
        ) : !featured ? (
          <ProductGridSkeleton count={8} />
        ) : featured.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-olive/20 py-16 text-center">
            <p className="text-ink/60">{t("no_products")}</p>
            <Link to="/store" className="mt-4 inline-block rounded-full bg-olive px-6 py-2.5 text-sm font-semibold text-ivory">{t("hero_cta2")}</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 lg:gap-6">
            {featured.slice(0, 8).map((p, i) => (
              <Reveal key={p.sku} delay={i * 50}><ProductCard product={p} index={i} /></Reveal>
            ))}
          </div>
        )}
      </section>

      <JourneySection />

      {/* SEASON */}
      {season && season.products?.length > 0 && (
        <section className="relative overflow-hidden bg-sand/40 py-16 sm:py-20">
          <div className="relative mx-auto max-w-7xl px-6">
            <Reveal>
              <span className="mb-2 inline-block rounded-full bg-olive/10 px-3 py-1 text-xs font-semibold text-olive">{season.season_ar}</span>
              <h2 className="font-display text-2xl font-bold text-ink sm:text-3xl lg:text-4xl">{t("season_title")}</h2>
              <p className="mt-3 max-w-xl text-sm text-ink/55 sm:text-base">{t("season_desc")}</p>
            </Reveal>
            <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4">
              {season.products.slice(0, 4).map((p, i) => (
                <Reveal key={p.sku} delay={i * 60}>
                  <Link to={`/product/${p.slug}`} className="group block overflow-hidden rounded-3xl border border-black/5 bg-white transition-[box-shadow,transform] duration-500 hover:-translate-y-1 hover:shadow-xl">
                    <div className="aspect-square overflow-hidden bg-sand">
                      <img src={p.images?.main} alt={p.name_ar} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="p-4">
                      <div className="font-display font-semibold text-ink">{L(p, "name")}</div>
                      <div className="mt-1 font-medium text-olive-dark">{fmtPrice(p.price)}</div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <OccasionDiscovery />

      {/* PALESTINIAN TABLE */}
      {table && (
        <section className="mx-auto max-w-7xl px-6 py-16 sm:py-20">
          <Reveal>
            <SectionHead eyebrow={t("table_eyebrow")} title={t("table_title")}
              desc={t("table_desc")} />
          </Reveal>
          <Reveal>
            <div data-testid="palestinian-table" className="relative overflow-hidden rounded-[2rem] border border-olive/10 shadow-xl">
              <img src={table.image} alt="المائدة الفلسطينية" loading="lazy" decoding="async" className="h-[340px] w-full object-cover sm:h-[520px]" />
              <div className="absolute inset-0 bg-black/10" />
              {table.items.map((it) => (
                <button key={it.sku} data-testid={`table-spot-${it.sku}`}
                  onClick={() => setActiveSpot(activeSpot?.sku === it.sku ? null : it)}
                  className="absolute -translate-x-1/2 translate-y-1/2"
                  style={{ left: `${it.x}%`, bottom: `${it.y}%` }}>
                  <span className="relative flex h-6 w-6 items-center justify-center">
                    <span className="absolute h-6 w-6 animate-pulse-ring rounded-full bg-ivory/70" />
                    <span className="h-4 w-4 rounded-full border-2 border-ivory bg-olive shadow-lg" />
                  </span>
                </button>
              ))}
              {activeSpot && activeSpot.product && (
                <div className="absolute bottom-4 right-4 left-4 flex items-center gap-4 rounded-2xl border border-olive/10 bg-ivory/95 p-3 backdrop-blur sm:right-auto sm:w-80">
                  <img src={activeSpot.product.image} alt={activeSpot.product.name_ar} className="h-16 w-16 rounded-xl object-cover" />
                  <div className="flex-1">
                    <div className="font-display font-semibold text-ink">{activeSpot.product.name_ar}</div>
                    <div className="text-sm text-olive">{fmtPrice(activeSpot.product.price)}</div>
                    <Link to={`/product/${activeSpot.product.slug}`} className="text-sm font-medium text-olive-dark link-underline">{t("discover_product")}</Link>
                  </div>
                  <button onClick={() => setActiveSpot(null)} className="text-ink/40"><X className="h-5 w-5" /></button>
                </div>
              )}
            </div>
          </Reveal>
        </section>
      )}

      {/* GEOGRAPHIC SOURCE — authentic terrains (tag + filter, never a section) */}
      <section className="relative bg-sand/40 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <SectionHead eyebrow={t("land_eyebrow")} title={t("land_title")} desc={t("land_desc")} />
          </Reveal>
          <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]">
            {/* Featured: جبال نابلس */}
            {nablus && (
              <Reveal>
                <Link to="/store?geo=nablus_mt" data-testid="terrain-featured-nablus"
                  className="group relative flex min-h-[260px] flex-col justify-end overflow-hidden rounded-[2rem] border border-gold/30 bg-olive-dark p-7 text-ivory sm:min-h-[320px]">
                  <img src="https://images.unsplash.com/photo-1650481424286-9d30bcc140e1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="جبال نابلس" loading="lazy" decoding="async"
                    className="absolute inset-0 h-full w-full object-cover opacity-45 transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-olive-deep/92 via-olive-deep/35 to-transparent" />
                  <div className="relative">
                    <span className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-gold px-3 py-1 text-xs font-bold text-olive-deep">
                      <Mountain className="h-3.5 w-3.5" /> {t("source_featured")}
                    </span>
                    <h3 className="font-display text-3xl font-extrabold">{nablus.name_ar}</h3>
                    <p className="mt-2 max-w-md text-ivory/85">{nablus.note_ar}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 font-medium">{t("view_source_products")} ({nablus.count}) <ArrowLeft className="h-4 w-4" /></span>
                  </div>
                </Link>
              </Reveal>
            )}
            {/* Other terrains as chips/cards */}
            <Reveal delay={90}>
              <div className="rounded-[2rem] border border-olive/15 bg-white p-6 sm:p-7">
                <h3 className="mb-1 font-display text-xl font-bold text-ink">{t("terrains_title")}</h3>
                <p className="mb-5 text-sm text-ink/55">{t("terrains_desc")}</p>
                <div className="flex flex-wrap gap-2.5">
                  {otherTerrains.map((g) => (
                    <Link key={g.key} to={`/store?geo=${g.key}`} data-testid={`terrain-chip-${g.key}`}
                      className="flex items-center gap-1.5 rounded-full border border-olive/20 bg-olive/5 px-4 py-2 text-sm font-medium text-olive-dark transition-colors hover:border-olive hover:bg-olive hover:text-ivory">
                      <Mountain className="h-4 w-4" /> {g.name_ar} <span className="opacity-60">({g.count})</span>
                    </Link>
                  ))}
                </div>
                <p className="mt-6 text-xs leading-relaxed text-ink/45">{t("terrains_note")}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BENTO GRID */}
      <section className="mx-auto max-w-7xl px-6 py-16 sm:py-20">
        <div className="grid gap-6 md:grid-cols-3">
          <Reveal className="md:col-span-2">
            <Link to="/basket-builder" data-testid="build-basket-teaser" className="group relative flex h-full min-h-[280px] flex-col justify-end overflow-hidden rounded-[2rem] bg-olive-dark p-6 text-ivory sm:min-h-[320px] sm:p-8">
              <img src="https://images.unsplash.com/photo-1783455442171-16dbde079c53?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="سلة" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-35 transition-transform duration-700 group-hover:scale-105" />
              <div className="relative">
                <Basket className="mb-3 h-8 w-8 text-earth" />
                <h3 className="font-display text-2xl font-bold">{t("basket_title")}</h3>
                <p className="mt-2 max-w-sm text-ivory/80">{t("basket_desc")}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 font-medium">{t("start_now")} <ArrowLeft className="h-4 w-4" /></span>
              </div>
            </Link>
          </Reveal>
          <Reveal delay={90}>
            <Link to="/basket-builder?type=gift" data-testid="gift-teaser" className="group relative flex h-full min-h-[280px] flex-col justify-end overflow-hidden rounded-[2rem] bg-earth p-6 text-ivory sm:min-h-[320px] sm:p-8">
              <img src="https://images.unsplash.com/photo-1702066054534-c1e7ceef9b2e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="gift" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-40 transition-transform duration-700 group-hover:scale-105" />
              <div className="relative">
                <Gift className="mb-3 h-8 w-8 text-white" />
                <h3 className="font-display text-2xl font-bold">{t("gift_title")}</h3>
                <p className="mt-2 text-white/85">{t("gift_desc")}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 font-medium">{t("pick_gift")} <ArrowLeft className="h-4 w-4" /></span>
              </div>
            </Link>
          </Reveal>
          {[
            { to: "/cook", icon: ChefHat, titleKey: "nav_cook", descKey: "d_cook", testid: "cook-teaser", bg: "bg-olive/8 text-olive-dark" },
            { to: "/box", icon: Box, titleKey: "nav_box", descKey: "d_box", testid: "box-teaser", bg: "bg-gold/15 text-olive-dark" },
            { to: "/nutrition", icon: Utensils, titleKey: "nav_nutrition", descKey: "d_nutrition", testid: "nutrition-teaser", bg: "bg-palred/8 text-olive-dark" },
          ].map((tile, i) => (
            <Reveal key={tile.to} delay={i * 70}>
              <Link to={tile.to} data-testid={tile.testid}
                className={`group flex h-full min-h-[180px] flex-col justify-between rounded-[2rem] border border-olive/10 p-7 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-olive/35 ${tile.bg}`}>
                <tile.icon className="h-7 w-7 text-olive" />
                <div>
                  <h3 className="font-display text-xl font-bold">{t(tile.titleKey)}</h3>
                  <p className="mt-1 text-sm text-ink/55">{t(tile.descKey)}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* HERITAGE */}
      {heritage.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 pb-20 sm:pb-24">
          <Reveal>
            <SectionHead eyebrow={t("heritage_eyebrow")} title={t("heritage_title")}
              desc={t("heritage_desc")}
              to="/heritage" toLabel={t("all_articles")} />
          </Reveal>
          <div className="grid gap-6 md:grid-cols-3">
            {heritage.slice(0, 3).map((h, i) => (
              <Reveal key={h.slug} delay={i * 70}>
                <Link to={`/heritage/${h.slug}`} className="group block overflow-hidden rounded-3xl border border-black/5 bg-white transition-shadow hover:shadow-lg">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={h.image} alt={h.title_ar} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-lg font-semibold text-ink transition-colors group-hover:text-olive">{h.title_ar}</h3>
                    <p className="mt-2 text-sm text-ink/55">{h.excerpt_ar}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
